import React, { useCallback, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { VscArrowDown } from 'react-icons/vsc';
import styled from 'styled-components';
import { StyledButton } from '../../Common/Buttons';
import { Flex, Text } from '../../Common/Layouts';
import AndroidGuideImages from '../Images/AndroidGuideImages';
import { FaKeyboard } from 'react-icons/fa';
import { useQuery } from '../../Common/Hooks';
import { useParams } from 'react-router-dom';

const Input = styled.input`
  opacity: 0;
`;
const List = styled.ol`
  list-style: none;
  padding: 0;

  counter-reset: item;
  li {
    position: relative;
    counter-increment: item;
    margin: 2rem 0;
    margin-left: 3.2rem;
    line-height: 19.2px;
    letter-spacing: 0.5px;
    font-size: 1rem;
    font-weight: 400;
    strong {
      font-weight: 600;
    }
  }
  li:before {
    content: counter(item);
    position: absolute;
    left: -3.2rem;
    top: 0;
    margin-right: 1rem;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    height: 1.9rem;
  }
`;

interface urlProps {
  step: string;
}
const steps = [
  'androidKeyboardInstallationGuide.steps.toggleKeyboard',
  'androidKeyboardInstallationGuide.steps.switchKeyboard',
  'androidKeyboardInstallationGuide.steps.headToTheApp',
];

const Description: React.FC = () => {
  const { t } = useTranslation();
  const useUrlParams = useParams<urlProps>();
  const [step, setStep] = useState(Number(useUrlParams.step));
  const hiddenInput = useRef<HTMLInputElement>(null);
  const query = useQuery();

  const activateKeyboard = () => {
    hiddenInput?.current?.focus();
    scrollTo();
  };

  const openApp = useCallback(() => {
    const appHost = query.get('host');
    const appScheme = query.get('scheme');
    const appPackage = query.get('package');
    if (appHost && appScheme && appPackage)
      window.location =
        `intent://${appHost}/#Intent;package=${appPackage};scheme=${appScheme};end;` as (
          | string
          | Location
        ) &
          Location;
  }, [query]);

  const handleIncrementStep = useCallback(() => {
    if (step === 2) {
      openApp();
      return;
    }
    setStep(step + 1);
  }, [openApp, step]);

  const stepDetail = useMemo(() => {
    if (step >= steps.length + 1) return null;
    return steps[step];
  }, [step]);

  return (
    <>
      <List>
        {stepDetail && (
          <div>
            <li style={{ counterReset: 'item ' + step }}>
              <Text fontWeight={700} fontSize="1.1rem">
                {t(stepDetail + '.title')}
              </Text>
              <Text>{t(stepDetail + '.description')}</Text>
              <AndroidGuideImages guidStep={step} />
            </li>
            {step === 1 && (
              <Flex align="center">
                <Flex align="center" width={45}>
                  <VscArrowDown size={36} onClick={activateKeyboard} />
                </Flex>
                <p>
                  {t('androidKeyboardInstallationGuide.pPart1')}
                  <span>
                    <FaKeyboard />
                  </span>
                  {t('androidKeyboardInstallationGuide.pPart2')}
                </p>
              </Flex>
            )}
            <Input type="text" ref={hiddenInput} />
            <StyledButton
              type="primary"
              width="100%"
              onClick={handleIncrementStep}
              colorType={3}
            >
              {t('androidKeyboardInstallationGuide.next')}
            </StyledButton>
          </div>
        )}
      </List>
    </>
  );
};

export default Description;
